<template>
    <b-container fluid class="ds-top-body py-3">
        <admin-page-header title="결과보고서 일괄 생성" :description="`프로젝트 ${project_code}`" :back="{ label: '목록으로', onClick: goList }">
            <template #actions>
                <!-- 실패 배지는 실행 뒤 표 갱신에도 남기고, 운영자가 새로고침할 때만 지운다 -->
                <b-button variant="dark-border" size="sm" :disabled="running" @click="failed_by_company = {}; loadRoster()">
                    새로고침
                </b-button>
            </template>
        </admin-page-header>

        <b-row class="mb-2">
            <b-col cols="12">
                <b-form-checkbox v-model="do_confirm" :disabled="running">
                    S3 업로드 + 확정
                    <span class="text-danger">— 확정하면 원청 포털에 즉시 공개됩니다.</span>
                    <span class="text-muted">해제하면 서버에 아무것도 쓰지 않고 ZIP 만 받습니다.</span>
                </b-form-checkbox>
            </b-col>
        </b-row>

        <b-row class="mb-2">
            <b-col cols="12" class="d-flex align-items-center">
                <span class="mr-2">생성 언어</span>
                <b-form-select v-model="report_lang" :options="report_lang_options" :disabled="running" size="sm" class="w-auto"></b-form-select>
                <span class="ml-2 text-muted">관리자 화면 언어와 무관하게 여기서 고른 언어로 생성·확정됩니다.</span>
            </b-col>
        </b-row>

        <!-- 실행 버튼은 진행률·중단 바로 위에 둔다 — 업체가 많아도 누른 자리에서 진행 상황이 보이게 -->
        <b-row class="mb-2">
            <b-col cols="12">
                <b-button variant="dark" :disabled="running || !selected_rows.length" @click="start">
                    {{ selected_rows.length }}개 업체 실행
                </b-button>
            </b-col>
        </b-row>

        <b-row v-if="running" class="mb-2">
            <b-col cols="12">
                <b-progress :max="run_total" height="1.4rem">
                    <b-progress-bar :value="run_index" :label="`${run_index} / ${run_total}`"></b-progress-bar>
                </b-progress>
                <div class="d-flex mt-1">
                    <small class="text-muted">{{ run_current_name }} — {{ phase_label }}</small>
                    <small class="ml-auto text-muted">누적 {{ formatMB(run_bytes) }}</small>
                    <b-button class="ml-3" variant="outline-danger" size="sm" @click="cancel_requested = true">
                        {{ cancel_requested ? '중단 중…' : '중단' }}
                    </b-button>
                </div>
            </b-col>
        </b-row>

        <b-row>
            <b-col cols="12">
                <!-- DS 표 규칙: 행 44 고정 · 칸마다 한 줄(넘치면 줄이고 전체는 title). 폭은 머리가 정한다 -->
                <b-table :items="rows" :fields="fields" responsive :busy="loading"
                         table-class="ds-table ds-table-fixed report-bulk-table"
                         show-empty empty-text="대상 업체가 없습니다.">
                    <template #table-busy>
                        <div class="text-center my-2">
                            <b-spinner small class="align-middle mr-2"></b-spinner>
                            <strong>불러오는 중</strong>
                        </div>
                    </template>
                    <template #head(selected)>
                        <b-form-checkbox :checked="all_selected" :disabled="running" @change="toggleAll"></b-form-checkbox>
                    </template>
                    <template #cell(selected)="data">
                        <b-form-checkbox v-model="data.item.selected" :disabled="running"></b-form-checkbox>
                    </template>
                    <!-- 번호는 표시 순서 = 실행 순서 -->
                    <template #cell(no)="data">
                        {{ data.index + 1 }}
                    </template>
                    <!-- 같은 이름 업체가 있어 업체 번호를 흐리게 붙인다 -->
                    <template #cell(company_name)="data">
                        <span class="ds-ellipsis" :title="`${data.value} (${data.item.company_id})`">{{ data.value }} <span class="ds-sub">{{ data.item.company_id }}</span></span>
                    </template>
                    <template #cell(mode)="data">
                        <span class="ds-tag" :class="{'ds-tone-danger': data.value !== 'DOC'}">{{ data.value === 'DOC' ? '서면평가' : '실사평가' }}</span>
                    </template>
                    <!-- 상태는 글자색 한 줄 — 실패 사유 · 남은 이력 번호는 title 로 -->
                    <template #cell(state)="data">
                        <span v-if="stateItems(data.item).length" class="ds-ellipsis"
                              :title="stateItems(data.item).map(s => s.title || s.text).join(' · ')">
                            <template v-for="(s, i) in stateItems(data.item)"><span v-if="i" :key="`sep-${i}`" class="ds-sub"> · </span><span :key="i" class="ds-status" :class="`ds-status-${s.tone}`">{{ s.text }}</span></template>
                        </span>
                        <span v-else class="ds-sub">-</span>
                    </template>
                </b-table>
            </b-col>
        </b-row>

        <!-- 렌더링 전용. reportData 가 준비된 뒤에만 마운트한다 — 뷰어가 mounted 에서 스스로 한 번 렌더하므로
             (ReportViewerSUPPLY.vue:123-131) null 데이터로 마운트하면 그 렌더가 실패한다.
             assessmentType 은 상수 '' 다. 업체마다 바꾸면 watcher(:92-100)가 previewPDF 를 추가로 돌린다.
             '' 이면 렌더러가 reportData.info.service_type 으로 판정한다 (report.supply.mixin.js:1825, :2165). -->
        <BulkReportContainer v-if="render_data"
                             ref="bulkContainer"
                             class="d-none"
                             :report_lang="report_lang"
                             :reportData="render_data"
                             :pdf_filename="render_filename"
                             reportMode="full"
                             assessmentType=""
                             @sendReportBlob="onViewerBlob"
        />
    </b-container>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {mapState, mapActions} from 'vuex';
import {zipSync} from 'fflate';
import BulkReportContainer from '@shared/components/admin/components/report/ReportMamagement/BulkReportContainer.vue';
// .cjs 사이드카는 require 로 부른다 — 기존 관례(evaluations.js:2, EditingSubComponentMixin.js:10)와 동일.
const {buildRunPlan, runBulk} = require('@shared/components/admin/pages/report-bulk-run.cjs');

const PHASE_LABEL = {
    data: '평가 데이터 조회',
    render: '보고서 생성',
    validate: '검증',
    upload: '업로드',
    confirm: '확정',
    skip: '건너뜀',
};

export default {
    name: 'ReportBulkRun',
    components: {BulkReportContainer},
    data() {
        return {
            loading: false,
            running: false,
            rows: [],
            do_confirm: true,
            // 보고서 언어는 화면(UI) 언어와 분리한다. 기본 ko.
            report_lang: 'ko',
            report_lang_options: [
                {value: 'ko', text: '한국어'},
                {value: 'en', text: 'English'},
                {value: 'ca', text: '中文'},
            ],
            cancel_requested: false,

            run_index: 0,
            run_total: 0,
            run_current_name: '',
            run_phase: '',
            run_bytes: 0,
            failed_by_company: {},

            // 렌더러에 넘길 현재 업체 데이터. 컨테이너는 이 값이 생겨야 마운트된다.
            render_data: null,
            render_filename: '',
            container_ready: false,
            mount_render_resolve: null,

            ev_code_list: null,
            // 목록에서 들어왔으면 그 URL(기간·페이지·검색 포함)로 돌아간다. 새로고침·직접 진입이면 null.
            list_return: null,
            // DS 표 규칙: 폭(px)을 적은 칸은 고정, 업체명 · 상태가 남는 폭을 나눠 갖는다
            fields: [
                {key: 'selected', label: '', thStyle: {width: '40px'}},
                {key: 'no', label: 'No.', thClass: 'ds-cell-no', tdClass: 'ds-cell-no', thStyle: {width: '56px'}},
                {key: 'company_name', label: '업체명'},
                {key: 'mode', label: '평가유형', thStyle: {width: '96px'}},
                {key: 'state', label: '상태'},
            ],
        };
    },
    computed: {
        ...mapState(['is_admin']),
        project_code() {
            return this.$route.query.pc;
        },
        selected_rows() {
            return this.rows.filter(r => r.selected);
        },
        all_selected() {
            return this.rows.length > 0 && this.rows.every(r => r.selected);
        },
        phase_label() {
            return PHASE_LABEL[this.run_phase] || '';
        },
    },
    created() {
        // 라우트 가드는 로그인만 본다(guard.js:45-49). 관리자 등급은 여기서 막는다.
        if (this.is_admin !== 4) {
            this.$EventBus.$emit('toast-failure', '권한 없음', '최고 관리자만 쓸 수 있는 화면입니다.');
            this.$router.replace({name: 'ProjectCodeList'});
            return;
        }
        if (!this.project_code) {
            // ProjectCodeList.created 가 urlError 로 '잘못된 접근' 안내를 띄운다
            this.$router.replace({name: 'ProjectCodeList', params: {urlError: true}});
            return;
        }
        this.loadRoster();
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            if (from.name === 'ProjectCodeList') vm.list_return = from.fullPath;
        });
    },
    mounted() {
        window.addEventListener('beforeunload', this.warnUnload);
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.warnUnload);
        // 실행 중 이탈하면 다음 업체를 시작하지 않는다.
        this.cancel_requested = true;
    },
    async beforeRouteLeave(to, from, next) {
        if (!this.running) return next();
        const _ok = await this.$showModal({
            type: 'confirm', state: 'warning', title: '일괄 생성이 진행 중입니다.',
            message: '이 화면을 떠나면 진행 중인 생성이 중단되고 ZIP 이 내려받아지지 않습니다. 이미 확정된 업체는 되돌려지지 않습니다. 떠나시겠습니까?',
        });
        next(!!_ok);
    },
    methods: {
        ...mapActions('evaluations', ['fetchEvCode']),
        warnUnload(e) {
            if (!this.running) return;
            e.preventDefault();
            e.returnValue = '';
        },
        // 표 배지 — 내부 단계 키(data 등)가 아니라 사람 말로
        failBadge(f) {
            return f.phase === 'skip' ? PHASE_LABEL.skip : `${PHASE_LABEL[f.phase] || f.phase} 실패`;
        },
        // 표 상태 칸 — 표에 다 못 싣는 실패 사유 · 남은 이력 번호 · 복구 실패는 title 에
        stateItems(item) {
            const _items = [];
            if (item.already_confirmed) _items.push({text: '이미 확정됨', tone: 'success'});
            if (item.confirm_blocked) _items.push({text: '서면평가 확정 필요', tone: 'warning'});
            const _f = this.failed_by_company[item.company_id];
            if (_f) {
                let _title = [this.failBadge(_f), _f.reason].filter(Boolean).join(': ');
                if (_f.released_history_id) _title += ` (이력 #${_f.released_history_id} 생성됨)`;
                if (_f.restore_failed) _title += ' — 기존 확정 복구 실패, 수동 확인 필요';
                _items.push({text: this.failBadge(_f), tone: _f.phase === 'skip' ? 'warning' : 'danger', title: _title});
            }
            return _items;
        },
        formatMB(bytes) {
            return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        },
        toggleAll(checked) {
            this.rows.forEach(r => { r.selected = checked; });
        },
        goList() {
            this.$router.push(this.list_return || {name: 'ProjectCodeList'});
        },
        async loadRoster() {
            this.loading = true;
            try {
                const [_roster, _history] = await Promise.all([
                    this.$axios('/report', {params: {project_code: this.project_code}}),
                    this.$axios('/report/history', {params: {project_code: this.project_code, with_alt: true}}),
                ]);
                this.rows = buildRunPlan(_roster.data, _history.data.result);
            } catch (error) {
                this.$showModal({
                    type: 'alert', state: 'error', title: '대상 목록을 불러오지 못했습니다.',
                    message: this.errorText(error),
                });
            } finally {
                this.loading = false;
            }
        },
        errorText(error) {
            return getErrorMessage(error, '') || (error && error.message ? error.message : String(error));
        },
        onViewerBlob() {
            if (this.mount_render_resolve) {
                this.mount_render_resolve();
                this.mount_render_resolve = null;
            }
        },
        // 컨테이너가 처음 마운트되면 뷰어가 폰트(8종 TTF)를 받은 뒤 스스로 한 번 렌더하고 sendReportBlob 을 쏜다.
        // 그게 끝나기 전에 우리가 generatePDF 를 부르면 같은 캔버스·문서를 두 렌더가 나눠 쓴다.
        // 그 렌더 결과는 버린다 — 대기가 목적이고, 덤으로 폰트 로딩까지 끝난 상태가 보장된다.
        waitForMountRender() {
            return new Promise(resolve => {
                this.mount_render_resolve = resolve;
                // 마운트 렌더가 실패하면(렌더러가 예외를 삼킨다) 이벤트가 오지 않는다. 무한 대기 금지.
                setTimeout(() => {
                    if (this.mount_render_resolve) {
                        this.mount_render_resolve = null;
                        resolve();
                    }
                }, 60000);
            });
        },
        buildIo() {
            return {
                fetchData: async (row) => {
                    const _res = await this.$axios('/report/data', {
                        params: {campaign_id: row.campaign_id, project_code: row.project_code, company_id: row.company_id},
                    });
                    // 상위집합 페이로드. 레거시(ReportBulkGenerator.vue:663-680)는 렌더러 판정에 따라 두 가지 모양을
                    // 따로 만들었는데, 그 판정이 컨테이너의 판정과 달라(fallbackRenderer 유무) 어긋나면 백지가 나왔다.
                    // 전부 넘기면 판정이 필요 없다 — 어느 렌더러든 자기가 읽는 것만 읽는다.
                    return {..._res.data, parser: _res.data.parser_result, ev_code_list: this.ev_code_list};
                },
                // 점수 게이트를 통과한 업체만 렌더러에 넣는다 (건너뛸 업체로 첫 마운트 렌더를 돌리지 않게)
                prepare: async (data) => {
                    this.render_data = data;
                    await this.$nextTick();
                    if (!this.container_ready) {
                        await this.waitForMountRender();
                        this.container_ready = true;
                    }
                },
                filename: (data, row) => {
                    // 이름 규칙은 report.naming.mixin.js:48-76 에 이미 있다. 레거시처럼 표를 복제하지 않고 그걸 읽는다.
                    const _viewer = this.$refs.bulkContainer && this.$refs.bulkContainer.$refs.resultReport;
                    const _base = _viewer && _viewer.report_filename ? _viewer.report_filename : '결과보고서';
                    const _company = data.info && data.info.company && data.info.company.company_name
                        ? data.info.company.company_name
                        : row.company_name;
                    return _company ? `${_base}_${_company}` : _base;
                },
                render: async (data, filename) => {
                    this.render_filename = filename;
                    await this.$nextTick();
                    const _container = this.$refs.bulkContainer;
                    const _doc = await _container.generatePDF();
                    // 컨테이너는 렌더 예외를 삼키고 undefined 를 돌려준다(다른 호출자 계약). 사유는 last_error 로 받는다.
                    if (!_doc && _container.last_error) throw _container.last_error;
                    return _doc;
                },
                toBlob: (doc, filename) => {
                    const _blob = doc.output('blob', {filename: `${filename}.pdf`});
                    // ZIP 은 메모리에 전부 올라간다. 운영자가 한계에 다가가는 걸 보고 있게 누적치를 노출한다.
                    this.run_bytes += _blob.size;
                    return _blob;
                },
                upload: async (row, blob, filename) => {
                    const _form = new FormData();
                    _form.append('campaign_id', row.campaign_id);
                    _form.append('company_id', row.company_id);
                    _form.append('project_code', row.project_code);
                    _form.append('report', new File([blob], `${filename}.pdf`, {type: 'application/pdf'}));
                    _form.append('en', this.report_lang !== 'ko');
                    _form.append('mode', row.mode);

                    const _res = await this.$axios.post('/report/release', _form, {
                        headers: {'Content-Type': 'multipart/form-data'},
                    });
                    return _res.data ? _res.data.report_history_id : null;
                },
                confirm: (reportId) => this.$axios({method: 'PUT', url: '/report/confirm', data: {report_id: reportId}}),
                unconfirm: (reportId) => this.$axios({method: 'DELETE', url: '/report/confirm', data: {report_id: reportId}}),
            };
        },
        async start() {
            const _targets = this.selected_rows;
            const _blocked = _targets.filter(r => this.do_confirm && r.confirm_blocked).length;
            const _reconfirm = _targets.filter(r => this.do_confirm && r.already_confirmed).length;

            const _lang_name = (this.report_lang_options.find(o => o.value === this.report_lang) || {}).text;
            const _warn = [`생성 언어: ${_lang_name}`];
            if (this.do_confirm) {
                _warn.push(`${_targets.length}개 업체의 보고서를 생성하고 확정합니다. 확정 즉시 원청 포털에 공개됩니다.`);
                if (_reconfirm) _warn.push(`이미 확정된 ${_reconfirm}개 업체는 기존 확정이 해제되고 새 보고서로 다시 확정됩니다.`);
                if (_blocked) _warn.push(`서면평가가 확정되지 않은 실사평가 ${_blocked}개 업체는 생성·ZIP 만 하고 확정은 건너뜁니다.`);
            } else {
                _warn.push(`${_targets.length}개 업체의 보고서를 생성해 ZIP 으로만 내려받습니다. 서버에는 아무것도 쓰지 않습니다.`);
            }

            const _h = this.$createElement;
            const _ok = await this.$showModal({
                type: 'confirm',
                state: this.do_confirm ? 'warning' : 'info',
                title: '결과보고서 일괄 생성',
                message: _h('div', {}, [
                    ..._warn.map(t => _h('p', {class: 'mb-1'}, t)),
                    _h('ul', {class: 'mb-0 pl-4', style: 'max-height: 12rem; overflow: auto;'}, _targets.map(r => _h('li', {}, [
                        r.company_name,
                        this.do_confirm && r.confirm_blocked ? _h('b-badge', {class: 'ml-1', props: {variant: 'warning'}}, '확정 차단') : null,
                        this.do_confirm && r.already_confirmed ? _h('b-badge', {class: 'ml-1', props: {variant: 'info'}}, '이미 확정') : null,
                    ]))),
                ]),
            });
            if (!_ok) return;

            this.running = true;
            this.cancel_requested = false;
            this.failed_by_company = {};
            this.run_index = 0;
            this.run_total = _targets.length;
            this.run_bytes = 0;
            this.container_ready = false;

            try {
                if (!this.ev_code_list) this.ev_code_list = await this.fetchEvCode();

                const _result = await runBulk(_targets, this.buildIo(), {
                    confirm: this.do_confirm,
                    cancelled: () => this.cancel_requested,
                    onProgress: (index, row, phase) => {
                        this.run_index = index;
                        this.run_current_name = row.company_name;
                        this.run_phase = phase;
                    },
                });

                this.run_index = this.run_total;
                _result.failed.forEach(f => { this.$set(this.failed_by_company, f.company_id, f); });
                // 화면을 떠난 뒤에는 ZIP·결과 모달을 다른 화면에 띄우지 않는다.
                if (this._isDestroyed) return;
                // 요약을 먼저 보인다. ZIP 이 실패(메모리 부족 등)해도 어느 업체가 이미 업로드·확정됐는지는 알아야 한다.
                await this.reportResult(_result);
                if (this._isDestroyed || !_result.files.length) return;
                try {
                    await this.downloadZip(_result.files);
                } catch (error) {
                    this.$showModal({
                        type: 'alert', state: 'error', title: 'ZIP 을 만들지 못했습니다.',
                        message: `${this.errorText(error)} — 업로드·확정 결과는 앞의 요약과 같습니다.`,
                    });
                }
            } catch (error) {
                this.$showModal({type: 'alert', state: 'error', title: '실행이 중단되었습니다.', message: this.errorText(error)});
            } finally {
                this.running = false;
                this.render_data = null;
                this.render_filename = '';
                this.container_ready = false;
                if (!this._isDestroyed) await this.loadRoster();
            }
        },
        async downloadZip(files) {
            if (!files.length) return;
            // zipSync 는 Uint8Array 만 받는다. 여기서 전부 메모리에 올라가므로 업체 수가 커지면 이 지점이 한계다.
            // ponytail: 실측 한계를 넘기면 fflate 의 AsyncZipDeflate 스트리밍으로 바꾼다. 지금은 단순함이 이긴다.
            const _entries = {};
            for (const _file of files) {
                _entries[_file.name] = new Uint8Array(await _file.blob.arrayBuffer());
            }

            const _blob = new Blob([zipSync(_entries, {level: 6})], {type: 'application/zip'});
            const _link = document.createElement('a');
            _link.href = URL.createObjectURL(_blob);
            _link.download = `결과보고서_${this.project_code}.zip`;
            document.body.appendChild(_link);
            _link.click();
            document.body.removeChild(_link);
            URL.revokeObjectURL(_link.href);
        },
        async reportResult(result) {
            const _h = this.$createElement;
            const _skipped = result.failed.filter(f => f.phase === 'skip').length;
            const _lines = [
                _h('p', {class: 'mb-1'}, `성공 ${result.done.length}건 / 실패 ${result.failed.length - _skipped}건${_skipped ? ` / 건너뜀 ${_skipped}건` : ''}${result.cancelled ? ' (중단됨)' : ''}`),
                _h('p', {class: 'mb-2'}, result.files.length
                    ? `내려받을 보고서 ${result.files.length}개 — 이 창을 닫으면 ZIP 으로 내려받습니다.`
                    : '내려받을 보고서가 없습니다.'),
            ];
            result.failed.forEach(f => {
                let _text = `${f.company_name} — ${PHASE_LABEL[f.phase] || f.phase}: ${f.reason}`;
                // 업로드 뒤에 죽었으면 서버에 이력 행이 남는다. 숨기지 않고 번호를 준다.
                if (f.released_history_id) _text += ` (이력 #${f.released_history_id} 생성됨)`;
                _lines.push(_h('p', {class: 'mb-0 small text-danger'}, f.restore_failed
                    ? [_text + ' ', _h('AppIcon', {props: {name: 'alert-triangle'}, class: 'mr-1'}), '기존 확정 복구 실패 — 수동 확인 필요']
                    : _text));
            });
            // 업로드·확정은 됐지만 로고 같은 장식이 빠진 업체
            (result.warned || []).forEach(w => {
                _lines.push(_h('p', {class: 'mb-0 small text-warning'}, `${w.company_name} — 확정됨, ${w.reason}`));
            });

            await this.$showModal({
                type: 'alert',
                state: result.failed.length ? 'warning' : 'success',
                title: '일괄 생성 완료',
                message: _h('div', {}, _lines),
            });
        },
    },
};
</script>

<style>
/* 업체명 · 상태 칸이 너무 좁아지지 않게 — 좁은 화면은 가로 스크롤 */
.report-bulk-table {
    min-width: 640px;
}
</style>
